import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, Card, Button, Space, Typography, Form, Input, Select, Tag, Progress,
  Modal, Switch, Radio, InputNumber, Divider, Tooltip, Alert, message
} from 'antd';
import { SearchOutlined, ReloadOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';

const { Text } = Typography;
const { Option } = Select;

// 工单状态
const STATUS = {
  PENDING: 'pending',
  PRODUCING: 'producing',
  DONE: 'done',
  PAUSED: 'paused',
  TERMINATED: 'terminated',
};
const STATUS_MAP = {
  [STATUS.PENDING]: { text: '待生产', color: 'default' },
  [STATUS.PRODUCING]: { text: '生产中', color: 'processing' },
  [STATUS.DONE]: { text: '生产完成', color: 'success' },
  [STATUS.PAUSED]: { text: '已暂停生产', color: 'warning' },
  [STATUS.TERMINATED]: { text: '已终止生产', color: 'error' },
};

const PRODUCTS = [
  { value: 'PROD_Q1', label: '智能摄像机Q1' },
  { value: 'PROD_P1', label: '云台机P1' },
  { value: 'PROD_F1', label: '烟雾报警器F1' },
];

const GROUPS = [
  { value: 'default', label: '默认分组' },
  { value: 'groupA', label: 'A组' },
  { value: 'groupB', label: 'B组' },
];

// mock数据
const mockData = [
  { key: '1', orderName: '摄像机Q1-批次001', orderId: 'WO20260401001', productName: '智能摄像机Q1', productId: 'PROD_Q1', productType: 'machine', totalAuth: 10000, tool: 'platform', produced: 3500, pending: 6500, status: STATUS.PRODUCING, group: '默认分组', alertEnabled: true, alertType: 'count', alertValue: 500 },
  { key: '2', orderName: '云台机P1-批次003', orderId: 'WO20260402003', productName: '云台机P1', productId: 'PROD_P1', productType: 'machine', totalAuth: 5000, tool: 'platform', produced: 5000, pending: 0, status: STATUS.DONE, group: '默认分组', alertEnabled: false },
  { key: '3', orderName: '报警器F1-外协', orderId: 'WO20260403005', productName: '烟雾报警器F1', productId: 'PROD_F1', productType: 'machine', totalAuth: 2000, tool: 'non-platform', produced: 800, pending: 1200, status: STATUS.PRODUCING, group: 'A组' },
  { key: '4', orderName: '摄像机Q1-批次002', orderId: 'WO20260401002', productName: '智能摄像机Q1', productId: 'PROD_Q1', productType: 'machine', totalAuth: 8000, tool: 'platform', produced: 0, pending: 8000, status: STATUS.PENDING, group: '默认分组', alertEnabled: true, alertType: 'percent', alertValue: 10 },
  { key: '5', orderName: '云台机P1-生产中', orderId: 'WO20260404001', productName: '云台机P1', productId: 'PROD_P1', productType: 'machine', totalAuth: 3000, tool: 'platform', produced: 1200, pending: 1800, status: STATUS.PRODUCING, group: 'B组', alertEnabled: true, alertType: 'count', alertValue: 200 },
  { key: '6', orderName: '摄像机Q1-终止单', orderId: 'WO20260405001', productName: '智能摄像机Q1', productId: 'PROD_Q1', productType: 'machine', totalAuth: 6000, tool: 'platform', produced: 2000, pending: 4000, status: STATUS.TERMINATED, group: '默认分组' },
  { key: '7', orderName: '云台方案-模组批次001', orderId: 'WO20260406001', productName: '智能云台摄像机方案', productId: 'P_ECO_001', productType: 'plan', totalAuth: 2000, tool: 'platform', produced: 1200, pending: 800, status: STATUS.PRODUCING, group: '默认分组' },
];

// 授权不足展示文本
const getAlertText = (record) => {
  if (record.tool === 'non-platform') return '——';
  if (!record.alertEnabled) return '不提醒';
  if (record.alertType === 'count') return `低于${record.alertValue}个，产测强提醒`;
  return `低于${record.alertValue}%，产测强提醒`;
};

const ProduceOrder = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertEnabled, setAlertEnabled] = useState(false);
  const [alertType, setAlertType] = useState('count');
  const [alertValue, setAlertValue] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [groupModalVisible, setGroupModalVisible] = useState(false);
  const [groupModalValue, setGroupModalValue] = useState(null);

  const openAlertModal = (record) => {
    setAlertEnabled(record.alertEnabled || false);
    setAlertType(record.alertType || 'count');
    setAlertValue(record.alertValue || null);
    setAlertVisible(true);
  };

  const handleAlertSave = () => {
    message.success('授权不足提醒已保存');
    setAlertVisible(false);
  };

  const confirmAction = (title, content, onOk) => {
    Modal.confirm({ title, content, okText: '确认', cancelText: '取消', onOk });
  };

  // 操作按钮渲染
  const renderActions = (record) => {
    const { tool, status } = record;

    if (tool === 'non-platform') {
      return (
        <Space size={0} split={<Divider type="vertical" style={{ margin: '0 4px' }} />}>
          <Button type="link" size="small" onClick={() => navigate(`/production/productionOrder/edit?id=${record.orderId}`)}>编辑工单</Button>
          <Button type="link" size="small" onClick={() => navigate(`/production/productionOrder/detail?id=${record.orderId}`)}>工单详情</Button>
        </Space>
      );
    }

    const editBtn = { key: 'edit', label: '编辑工单', onClick: () => navigate(`/production/productionOrder/edit?id=${record.orderId}`) };
    const detailBtn = { key: 'detail', label: '工单详情', onClick: () => navigate(`/production/productionOrder/detail?id=${record.orderId}`) };
    const terminateBtn = { key: 'terminate', label: '终止生产', danger: true, onClick: () => confirmAction('终止生产', '终止生产不可恢复，系统会反还你的授权余额。你确认要停止吗？', () => message.success('已终止')) };

    let buttons = [];

    if (status === STATUS.PENDING || status === STATUS.PRODUCING) {
      buttons = [detailBtn, editBtn, terminateBtn];
    } else if (status === STATUS.DONE || status === STATUS.TERMINATED) {
      buttons = [detailBtn];
    }

    return (
      <Space size={0} split={<Divider type="vertical" style={{ margin: '0 4px' }} />}>
        {buttons.map(b => (
          <Button key={b.key} type="link" size="small" danger={b.danger} onClick={b.onClick}>{b.label}</Button>
        ))}
      </Space>
    );
  };

  const columns = [
    {
      title: '工单信息', key: 'orderInfo', width: 180,
      render: (_, r) => (<div><div style={{ fontWeight: 500 }}>{r.orderName}</div><Text type="secondary" style={{ fontSize: 12 }}>{r.orderId}</Text></div>),
    },
    {
      title: '生产产品', key: 'product', width: 150,
      render: (_, r) => (<div><div>{r.productName}</div><Text type="secondary" style={{ fontSize: 12 }}>{r.productId}</Text></div>),
    },
    {
      title: '设备类型', dataIndex: 'productType', key: 'productType', width: 110,
      render: (v) => (
        v === 'plan'
          ? <Tag color="purple" style={{ border: 'none' }}>方案产品</Tag>
          : <Tag color="blue" style={{ border: 'none' }}>整机产品</Tag>
      ),
    },
    {
      title: '生产工具', dataIndex: 'tool', width: 110,
      render: (v) => v === 'platform' ? <Tag color="blue">平台产测</Tag> : <Tag>非平台产测</Tag>,
    },
    { title: '授权总数', dataIndex: 'totalAuth', width: 100, sorter: (a, b) => a.totalAuth - b.totalAuth, showSorterTooltip: { title: '点击排序' } },
    {
      title: '生产进度', key: 'progress', width: 280,
      render: (_, r) => {
        const isNonPlatform = r.tool === 'non-platform';
        const produced = isNonPlatform ? 0 : r.produced;
        const pending = isNonPlatform ? r.totalAuth : r.pending;
        const pct = r.totalAuth > 0 ? Math.round((produced / r.totalAuth) * 100) : 0;
        return (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
              <span>已生产 <Text strong>{produced.toLocaleString()}</Text> / {r.totalAuth.toLocaleString()}</span>
              <Text type="secondary">待生产 {pending.toLocaleString()}</Text>
            </div>
            <Progress percent={pct} size="small" />
          </div>
        );
      },
    },
    {
      title: '工单状态', dataIndex: 'status', width: 120,
      render: (v, r) => { const s = r.tool === 'non-platform' ? STATUS_MAP[STATUS.PENDING] : STATUS_MAP[v]; return <Tag color={s.color}>{s.text}</Tag>; },
    },
    { title: '生产分组', dataIndex: 'group', width: 100 },
    {
      title: '授权不足', key: 'alert', width: 200,
      render: (_, r) => (
        <Space>
          <span>{getAlertText(r)}</span>
          {r.tool === 'platform' && (
            <Tooltip title="修改提醒设置">
              <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openAlertModal(r)} />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: '操作', key: 'action', width: 200, fixed: 'right',
      render: (_, r) => renderActions(r),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}><Typography.Title level={4}>生产工单</Typography.Title></div>

      {/* 查询条件 */}
      <Card bordered={false} style={{ marginBottom: 8, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }} bodyStyle={{ padding: '12px 0' }}>
          <Form form={form} layout="inline" style={{ flexWrap: 'wrap', gap: '8px 0' }}>
          <Form.Item style={{ marginRight: 8 }}>
            <Space.Compact>
              <Form.Item name="orderSearchType" initialValue="name" noStyle>
                <Select style={{ width: 100 }}>
                  <Option value="name">工单名称</Option>
                  <Option value="id">工单ID</Option>
                </Select>
              </Form.Item>
              <Form.Item name="orderKeyword" noStyle>
                <Input placeholder="请输入" allowClear style={{ width: 200 }} />
              </Form.Item>
            </Space.Compact>
          </Form.Item>
          <Form.Item style={{ marginRight: 8 }}>
            <Space.Compact>
              <Form.Item name="prodSearchType" initialValue="name" noStyle>
                <Select style={{ width: 100 }}>
                  <Option value="name">产品名称</Option>
                  <Option value="id">产品ID</Option>
                </Select>
              </Form.Item>
              <Form.Item shouldUpdate={(p, c) => p.prodSearchType !== c.prodSearchType} noStyle>
                {({ getFieldValue }) => getFieldValue('prodSearchType') === 'id'
                  ? <Form.Item name="prodKeyword" noStyle><Input placeholder="产品ID" allowClear style={{ width: 200 }} /></Form.Item>
                  : <Form.Item name="prodKeyword" noStyle><Select placeholder="请选择产品" allowClear showSearch optionFilterProp="label" style={{ width: 200 }} options={PRODUCTS} /></Form.Item>
                }
              </Form.Item>
            </Space.Compact>
          </Form.Item>
          <Form.Item name="group" style={{ marginRight: 8 }}>
            <Select placeholder="生产分组" allowClear style={{ width: 130 }} options={GROUPS} />
          </Form.Item>
          <Form.Item name="tool" style={{ marginRight: 8 }}>
            <Select placeholder="生产工具" allowClear style={{ width: 130 }}>
              <Option value="platform">平台产测</Option>
              <Option value="non-platform">非平台产测</Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />}>查询</Button>
              <Button icon={<ReloadOutlined />} onClick={() => form.resetFields()}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* 列表 */}
      <Card bordered={false}>
        <div style={{ marginBottom: 12, display: 'flex', gap: 8 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/production/productionOrder/create')}>创建工单</Button>
          <Button disabled={selectedRowKeys.length === 0} onClick={() => { setGroupModalValue(null); setGroupModalVisible(true); }}>修改工单分组</Button>
        </div>
        <Table
          rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
          columns={columns}
          dataSource={mockData}
          rowKey="key"
          scroll={{ x: 'max-content' }}
          pagination={{ showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }}
          style={{ whiteSpace: 'nowrap' }}
        />
      </Card>

      {/* 授权不足提醒弹窗 */}
      <Modal
        title="授权不足—产测工具提醒"
        open={alertVisible}
        onCancel={() => setAlertVisible(false)}
        footer={
          <Space>
            <Button onClick={() => setAlertVisible(false)}>取消</Button>
            <Button type="primary" onClick={handleAlertSave}>保存</Button>
          </Space>
        }
        destroyOnClose
      >
        <div style={{ marginBottom: 16 }}>
          <span style={{ marginRight: 8 }}>授权不足提醒：</span>
          <Switch checked={alertEnabled} onChange={setAlertEnabled} />
        </div>
        {alertEnabled && (
          <>
            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>授权剩余</div>
              <Space>
                <Radio.Group value={alertType} onChange={(e) => { setAlertType(e.target.value); setAlertValue(null); }}>
                  <Radio value="count">剩余数量</Radio>
                  <Radio value="percent">剩余占工单比</Radio>
                </Radio.Group>
                <InputNumber
                  min={1}
                  max={alertType === 'percent' ? 100 : undefined}
                  value={alertValue}
                  onChange={setAlertValue}
                  addonAfter={alertType === 'percent' ? '%' : '个'}
                  style={{ width: 150 }}
                />
              </Space>
            </div>
          </>
        )}
      </Modal>

      {/* 修改工单分组弹窗 */}
      <Modal
        title="修改工单分组"
        open={groupModalVisible}
        onCancel={() => setGroupModalVisible(false)}
        onOk={() => { message.success('工单分组已修改'); setGroupModalVisible(false); setSelectedRowKeys([]); }}
        okText="保存"
        cancelText="取消"
        destroyOnClose
      >
        <Alert type="info" showIcon style={{ marginBottom: 16, borderRadius: 6 }} message="工单分组决定了哪些产测账号可以操作该工单，修改分组后，仅该分组下的账号有权进行生产。" />
        <div style={{ marginBottom: 16 }}>已选工单：<span style={{ fontWeight: 500 }}>{selectedRowKeys.length}</span> 个</div>
        <div>
          <span style={{ marginRight: 8 }}>产测分组：</span>
          <Select placeholder="请选择分组" value={groupModalValue} onChange={setGroupModalValue} style={{ width: 200 }} options={GROUPS} />
        </div>
      </Modal>
    </div>
  );
};

export default ProduceOrder;
